import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';

/**
 * Banner boven een gedeelde chart (spec 2026-10-20-chart-share §4.4/§4.6):
 * - member: ingelogde kijker in de chartomgeving ("niet in je library" + opslaan);
 * - guest: de publieke gastpagina (uitnodiging om in te loggen of te registreren).
 */
@Component({
  selector: 'hd-shared-chart-banner',
  standalone: true,
  imports: [SharedModule, TranslocoModule],
  templateUrl: './hd-shared-chart-banner.component.html',
  styleUrl: './hd-shared-chart-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[attr.data-mode]': 'mode()' },
})
export class HdSharedChartBannerComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly mode = input.required<'member' | 'guest'>();
  readonly chartName = input('');
  /** Voornaam van de deler; null = onbekend (oudere links). */
  readonly sharedByName = input<string | null>(null);
  /** Gast: registreren aanbieden (alleen wanneer publieke registratie open staat). */
  readonly showRegister = input(false);
  /** Lid: opslaan mogelijk? Enkel een standaardchart; anders een melding i.p.v. de knop. */
  readonly saveAvailable = input(true);

  readonly saveRequested = output<void>();
  readonly loginRequested = output<void>();
  readonly registerRequested = output<void>();

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const name = this.sharedByName();
    const translate = (key: string, params?: Record<string, unknown>) =>
      this.transloco.translate<string>(`humanDesign.chartShare.banner.${key}`, params, language);
    return {
      member: name ? translate('member', { name }) : translate('memberAnonymous'),
      save: translate('save'),
      saveUnavailable: translate('saveUnavailable'),
      guestTitle: name ? translate('guestTitle', { name }) : translate('guestTitleAnonymous'),
      guestMessage: translate('guestMessage'),
      login: translate('login'),
      register: translate('register'),
    };
  });
}
